import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  permissions: {
    behavioral: boolean;
    cognitive: boolean;
  };
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
  is_active: boolean;
  request_count: number;
}

export interface CreateApiKeyResponse {
  id: string;
  name: string;
  key_prefix: string;
  key: string; // Only returned on creation
  permissions: {
    behavioral: boolean;
    cognitive: boolean;
  };
  created_at: string;
  is_active: boolean;
}

export function useApiKeys() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fetchApiKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('manage-api-keys', {
        body: { action: 'list' },
      });

      if (response.error) throw response.error;
      
      if (response.data?.success) {
        setApiKeys(response.data.data || []);
      } else {
        throw new Error(response.data?.error || 'Failed to fetch API keys');
      }
    } catch (error: any) {
      console.error('Error fetching API keys:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch API keys',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const createApiKey = useCallback(async (
    name: string,
    permissions: { behavioral: boolean; cognitive: boolean }
  ): Promise<CreateApiKeyResponse | null> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('manage-api-keys', {
        body: { action: 'create', name, permissions },
      });

      if (response.error) throw response.error;
      
      if (response.data?.success) {
        // Refresh the list
        await fetchApiKeys();
        return response.data.data as CreateApiKeyResponse;
      } else {
        throw new Error(response.data?.error || 'Failed to create API key');
      }
    } catch (error: any) {
      console.error('Error creating API key:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create API key',
        variant: 'destructive',
      });
      return null;
    }
  }, [fetchApiKeys, toast]);

  const deleteApiKey = useCallback(async (keyId: string): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('manage-api-keys', {
        body: { action: 'delete', keyId },
      });

      if (response.error) throw response.error;
      
      if (response.data?.success) {
        setApiKeys(prev => prev.filter(k => k.id !== keyId));
        toast({
          title: 'API Key Deleted',
          description: 'The API key has been permanently deleted',
        });
        return true;
      } else {
        throw new Error(response.data?.error || 'Failed to delete API key');
      }
    } catch (error: any) {
      console.error('Error deleting API key:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete API key',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  const toggleApiKey = useCallback(async (keyId: string, isActive: boolean): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('manage-api-keys', {
        body: { action: 'toggle', keyId, isActive },
      });

      if (response.error) throw response.error;
      
      if (response.data?.success) {
        setApiKeys(prev => 
          prev.map(k => k.id === keyId ? { ...k, is_active: isActive } : k)
        );
        toast({
          title: isActive ? 'API Key Activated' : 'API Key Deactivated',
          description: isActive 
            ? 'The API key is now active and can be used' 
            : 'The API key has been deactivated',
        });
        return true;
      } else {
        throw new Error(response.data?.error || 'Failed to update API key');
      }
    } catch (error: any) {
      console.error('Error toggling API key:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update API key',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  return {
    apiKeys,
    isLoading,
    fetchApiKeys,
    createApiKey,
    deleteApiKey,
    toggleApiKey,
  };
}
